import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, LogOut, Wifi, Loader2, Trophy, Star, Gift, 
  Package, Settings, ArrowRight, Zap, Award, Clock, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const LEVEL_ICONS = {
  1: '🌱',
  2: '⭐',
  3: '🔍',
  4: '🦸',
  5: '👑'
};

const LEVEL_COLORS = {
  1: 'from-slate-400 to-slate-500',
  2: 'from-emerald-400 to-emerald-600',
  3: 'from-blue-400 to-blue-600',
  4: 'from-purple-400 to-purple-600',
  5: 'from-amber-400 to-amber-600'
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [rewardLevels, setRewardLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchRewardLevels();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRewardLevels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rewards/levels`);
      setRewardLevels(response.data.levels || []);
    } catch (error) {
      console.error('Error fetching reward levels:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-96 h-96 bg-violet-500/10 -top-48 -left-48 animate-float" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-10 h-10 rounded-full object-cover shadow-lg"
              />
              <span className="text-xl font-bold text-slate-900">TapTag</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/llogaria">
                <Button variant="ghost" className="text-slate-600">
                  <Package className="w-4 h-4 mr-2" />
                  Objektet
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Dil
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-4 relative z-10">
        <div className="container-app max-w-4xl">
          {/* Profile Header */}
          <Card className="border-0 shadow-xl overflow-hidden mb-8">
            <div className={`bg-gradient-to-r ${LEVEL_COLORS[profile?.level || 1]} p-8 text-white`}>
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-5xl">
                  {LEVEL_ICONS[profile?.level || 1]}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold mb-1">{profile?.name}</h1>
                  <p className="text-white/80">{profile?.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-white/20 text-white border-0 text-sm">
                      <Trophy className="w-4 h-4 mr-1" />
                      Niveli {profile?.level}: {profile?.level_name}
                    </Badge>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold">{profile?.total_points || 0}</div>
                  <div className="text-white/80">Pikë Totale</div>
                </div>
              </div>
            </div>

            {/* Progress to Next Level */}
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Progresi për nivelin e ardhshëm</span>
                <span className="text-sm font-semibold text-violet-600">
                  {profile?.total_points || 0} / {profile?.next_level_points || 0} pikë
                </span>
              </div>
              <Progress value={profile?.progress_percent || 0} className="h-3" />
              <p className="text-xs text-slate-500 mt-2">
                {profile?.progress_percent < 100 
                  ? `Të mbeten ${(profile?.next_level_points || 0) - (profile?.total_points || 0)} pikë për nivelin e ardhshëm`
                  : 'Ke arritur nivelin maksimal! 🎉'}
              </p>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-3">
                  <Wifi className="w-7 h-7 text-violet-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{profile?.scans_made || 0}</div>
                <div className="text-sm text-slate-500">Skanime të Bëra</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{profile?.total_points || 0}</div>
                <div className="text-sm text-slate-500">Pikë të Grumbulluara</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-7 h-7 text-amber-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{profile?.rewards_earned?.length || 0}</div>
                <div className="text-sm text-slate-500">Shpërblime të Fituara</div>
              </CardContent>
            </Card>
          </div>

          {/* Reward Levels */}
          <Card className="border-0 shadow-xl mb-8">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Award className="w-6 h-6 text-violet-600" />
                Nivelet e Shpërblimeve
              </h2>
              
              <div className="space-y-4">
                {rewardLevels.map((level, index) => {
                  const isCurrentLevel = level.level === profile?.level;
                  const isAchieved = level.level <= profile?.level;
                  
                  return (
                    <div 
                      key={level.level}
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        isCurrentLevel 
                          ? 'border-violet-500 bg-violet-50' 
                          : isAchieved 
                            ? 'border-emerald-300 bg-emerald-50/50' 
                            : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                        isAchieved ? 'bg-white shadow' : 'bg-slate-200'
                      }`}>
                        {LEVEL_ICONS[level.level]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">Niveli {level.level}: {level.name}</span>
                          {isCurrentLevel && (
                            <Badge className="bg-violet-500 text-white">Aktuali</Badge>
                          )}
                          {isAchieved && !isCurrentLevel && (
                            <Badge className="bg-emerald-500 text-white">✓ Arritur</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{level.min_points} pikë të nevojshme</p>
                        {level.reward && (
                          <p className="text-sm text-amber-600 font-medium mt-1">
                            🎁 {level.reward.message}
                          </p>
                        )}
                      </div>
                      {!isAchieved && (
                        <div className="text-right">
                          <span className="text-sm text-slate-500">
                            {level.min_points - (profile?.total_points || 0)} pikë mbeten
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Rewards Earned */}
          {profile?.rewards_earned?.length > 0 && (
            <Card className="border-0 shadow-xl mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Gift className="w-6 h-6 text-amber-600" />
                  Shpërblimet e Fituara
                </h2>
                
                <div className="space-y-3">
                  {profile.rewards_earned.map((reward, index) => (
                    <div 
                      key={reward.id || index}
                      className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
                    >
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl">
                        🎁
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{reward.message}</p>
                        <p className="text-sm text-slate-500">
                          Fituar në nivelin {reward.level}: {reward.level_name}
                        </p>
                      </div>
                      <Badge className={reward.claimed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {reward.claimed ? 'Marrë' : 'Për të marrë'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scan History */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Clock className="w-6 h-6 text-slate-600" />
                Historiku i Skanimeve
              </h2>
              
              {profile?.scan_history?.length > 0 ? (
                <div className="space-y-3">
                  {profile.scan_history.map((scan, index) => (
                    <div 
                      key={scan.id || index}
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200"
                    >
                      <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{scan.object_name}</p>
                        <p className="text-sm text-slate-500">
                          Pronari: {scan.sticker_owner_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-emerald-100 text-emerald-700">
                          +{scan.points_earned} pikë
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(scan.scanned_at).toLocaleDateString('sq-AL')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Wifi className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 mb-4">Nuk ke skanuar ende asnjë stiker</p>
                  <p className="text-sm text-slate-400">
                    Skanoni stikerët e objekteve të humbura për të grumbulluar pikë!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200">
            <h3 className="font-bold text-violet-900 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" />
              Si të fitosh pikë?
            </h3>
            <p className="text-violet-700 text-sm">
              Çdo herë që skanon një stiker të një objekti të humbur dhe kontakton pronarin, 
              fiton <strong>10 pikë</strong>. Grumbulloji pikët për të arritur nivele më të larta 
              dhe fito shpërblime si stikerë falas dhe TapTag Key Chain!
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 bg-slate-900 relative z-10">
        <div className="container-app">
          <div className="flex items-center justify-center gap-4">
            <img 
              src={TAPTAG_LOGO}
              alt="TAPTAG Logo"
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-sm text-slate-400">TAPTAG © 2024</span>
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              <Settings className="w-3 h-3" />
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfilePage;
